import { scaleToLongEdge } from '@/domain/menu-import/capture-files';
import {
  LOGO_JPEG_QUALITY,
  LOGO_MAX_EDGE,
  logoOutputType,
} from '@/domain/store-setup/logo-file';
import type { TLogoOutputType } from '@/domain/store-setup/logo-file';
import {
  context2d,
  createCanvas,
  releaseCanvas,
} from '@/lib/menu-capture/capture-dom';
import { decodeBlob } from '@/lib/menu-capture/image-processor';

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: TLogoOutputType,
  quality: number | undefined
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not encode the logo'));
      },
      type,
      quality
    );
  });
}

/**
 * The logo as it is uploaded (plan §4.2, D10): decoded by the browser, scaled
 * to at most 1024 px (never upscaled), PNG kept as PNG, anything else a JPEG
 * q0.9 on white. The canvas and the decoded image are released before
 * returning. Throws when the browser cannot read the file.
 */
export async function prepareLogoImage(file: File): Promise<Blob> {
  const type = logoOutputType(file);
  const decoded = await decodeBlob(file);
  const { width, height } = scaleToLongEdge(
    decoded.width,
    decoded.height,
    LOGO_MAX_EDGE
  );
  let canvas: HTMLCanvasElement | null = null;
  try {
    try {
      canvas = createCanvas(width, height);
      const context = context2d(canvas);
      if (type === 'image/jpeg') {
        // JPEG has no transparency: transparent pixels would turn black.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      }
      context.drawImage(decoded.source, 0, 0, width, height);
    } finally {
      decoded.release();
    }
    return await canvasToBlob(
      canvas,
      type,
      type === 'image/jpeg' ? LOGO_JPEG_QUALITY : undefined
    );
  } finally {
    releaseCanvas(canvas);
  }
}
